// src/hooks/pending-orders/usePendingOrdersProducts.ts
// Hook para manejo de productos en PendingOrdersForm

import { useState, useCallback, useRef } from 'react'
import axiosInstance from '../../api/axiosInstance'
import type { OrderDetail, PendingOrderFormData } from '../../types/pendingOrdersFormTypes'
import { calculateTotals as computeOrderLineTotals } from '../../utils/orderFormUtils'
import { mapApiProductToOrderDetails } from '../../utils/productStockDisplayUtils'
import { FLEXY_PRODUCTS_MULTIPLE_PATH } from '../../constants/productsApiPaths'

interface UsePendingOrdersProductsProps {
  orderDetail?: OrderDetail | null
  formData: PendingOrderFormData
  setFormData: React.Dispatch<React.SetStateAction<PendingOrderFormData>>
  onTipoServicioChange?: (tipoServicio: string) => void
  onFormReadyChange?: (ready: boolean) => void
  clienteNeto?: boolean
}

export function usePendingOrdersProducts({
  orderDetail,
  formData,
  setFormData,
  onTipoServicioChange,
  onFormReadyChange,
  clienteNeto = false
}: UsePendingOrdersProductsProps) {
  const [productDetails, setProductDetails] = useState<{ [key: string]: any }>({})
  const [loadingDetails, setLoadingDetails] = useState<{ [key: string]: boolean }>({})
  const [expandedCombos, setExpandedCombos] = useState<{ [key: string]: boolean }>(() => {
    const initialExpanded: { [key: string]: boolean } = {}
    if (orderDetail?.lineItems) {
      orderDetail.lineItems.forEach(item => {
        initialExpanded[item.product_id] = true
      })
    }
    return initialExpanded
  })

  // Refs para evitar múltiples cargas
  const lastLoadedSkusRef = useRef<string>('')
  const isLoadingRef = useRef<boolean>(false)

  // Función para cargar los detalles de los productos
  const loadProductDetails = useCallback(async () => {
    if (!orderDetail?.lineItems?.length) {
      isLoadingRef.current = false
      return
    }

    // Generar clave única basada en los SKUs
    const allSkus: string[] = []

    // Agregar SKUs de productos principales
    orderDetail.lineItems.forEach(item => {
      allSkus.push(item.product_id)
    })

    // Agregar SKUs de componentes de combos
    orderDetail.lineItems.forEach(item => {
      if (item.is_combo === 1 && item.components) {
        item.components.forEach((component: any) => {
          allSkus.push(component.sku)
        })
      }
    })

    // Eliminar duplicados
    const uniqueSkus: string[] = Array.from(new Set(allSkus))
    const skusKey = uniqueSkus.sort().join(',')

    // Evitar consultas duplicadas
    if (isLoadingRef.current || lastLoadedSkusRef.current === skusKey) {
      console.log('⚠️ Productos ya cargados o cargando, omitiendo carga duplicada')
      return
    }

    try {
      isLoadingRef.current = true
      lastLoadedSkusRef.current = skusKey
      console.log('🚀 Iniciando carga de detalles de productos...')
      const newDetails: { [key: string]: any } = {}
      const newLoadingDetails: { [key: string]: boolean } = {}

      // Variables para almacenar las dimensiones máximas y sumas
      let maxLargo = 0
      let maxAncho = 0
      let totalPeso = 0
      let totalAlto = 0
      let productoMaxLargo: any = null
      let productoMaxAncho: any = null
      let todosTienenLargo = true
      let todosTienenAncho = true
      let todosTienenPeso = true
      let todosTienenAlto = true

      // Marcar todos los productos como cargando
      uniqueSkus.forEach(sku => {
        newLoadingDetails[sku] = true
        setLoadingDetails(prev => ({ ...prev, [sku]: true }))
      })

      try {
        console.log('Cargando detalles de todos los productos:', uniqueSkus)

        // Una sola consulta múltiple para obtener TODA la información
        const token = localStorage.getItem('accessToken')
        const response = await axiosInstance.get(`${FLEXY_PRODUCTS_MULTIPLE_PATH}?skus=${uniqueSkus.join(',')}`, {
          headers: { Authorization: `Bearer ${token}` }
        })

        if (response.data && response.data.products) {
          // Procesar cada producto de la respuesta
          response.data.products.forEach((product: any) => {
            const sku = product.sku?.toString().replace(/^0+/, '') || ''
            newDetails[sku] = mapApiProductToOrderDetails({ ...product, sku })
          })
        }

        // Procesar combos para calcular stock mínimo
        orderDetail.lineItems.forEach(item => {
          if (item.is_combo === 1 && item.components) {
            const stocks: number[] = []

            // Calcular stock mínimo de componentes
            item.components.forEach((component: any) => {
              const componentDetails = newDetails[component.sku]
              if (componentDetails) {
                const stockValue = componentDetails.stock || componentDetails.stock_quantity || 0
                stocks.push(stockValue)
              } else {
                stocks.push(0)
              }
            })

            // Actualizar stock del combo con el mínimo
            if (stocks.length > 0) {
              const minStock = Math.min(...stocks)
              console.log('Stocks de componentes:', stocks)
              console.log('Stock mínimo para el combo:', minStock)

              if (newDetails[item.product_id]) {
                newDetails[item.product_id] = {
                  ...newDetails[item.product_id],
                  stock: minStock,
                  stock_quantity: minStock
                }
              }
            }
          }
        })
      } catch (error) {
        console.error('Error al cargar productos múltiples:', error)
        
        // Fallback: consultas individuales cuando falla la búsqueda múltiple
        console.log('🔄 Intentando cargar productos individualmente...')
        for (const item of orderDetail.lineItems) {
          let response
          try {
            newLoadingDetails[item.product_id] = true
            setLoadingDetails(prev => ({ ...prev, [item.product_id]: true }))
            
            const token = localStorage.getItem('accessToken')
            response = await axiosInstance.get(`/products/${item.product_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            })
            
            if (response.data) {
              const sku = response.data.sku?.toString().replace(/^0+/, '') || ''
              newDetails[item.product_id] = mapApiProductToOrderDetails({
                ...response.data,
                sku
              })
              
              // Procesar componentes si es un combo
              if (response.data.is_combo === 1 && response.data.components) {
                const stocks: number[] = []
                
                for (const component of response.data.components) {
                  const compSku = component.sku
                  // Usar newDetails en lugar de productDetails para evitar dependencias
                  if (!newDetails[compSku] && compSku) {
                    setLoadingDetails(prev => ({ ...prev, [compSku]: true }))
                    
                    try {
                      const componentResponse = await axiosInstance.get(`/products/${compSku}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      })
                      
                      if (componentResponse.data) {
                        const mappedComp = mapApiProductToOrderDetails({
                          ...componentResponse.data,
                          sku: compSku
                        })
                        stocks.push(mappedComp.stock)
                        newDetails[compSku] = mappedComp
                      }
                    } catch (componentError) {
                      console.error('Error al cargar componente:', compSku, componentError)
                      stocks.push(0)
                    } finally {
                      setLoadingDetails(prev => ({ ...prev, [compSku]: false }))
                    }
                  } else if (newDetails[compSku]) {
                    const stockValue = newDetails[compSku].stock || newDetails[compSku].stock_quantity || 0
                    stocks.push(stockValue)
                  }
                }
                
                // Calcular stock mínimo para el combo
                if (stocks.length > 0) {
                  const minStock = Math.min(...stocks)
                  newDetails[item.product_id] = {
                    ...newDetails[item.product_id],
                    stock: minStock,
                    stock_quantity: minStock
                  }
                }
              }
            }
          } catch (err) {
            console.error('Error al cargar producto individual:', item.product_id, err)
            newDetails[item.product_id] = {
              ...response?.data,
              sku: item.product_id,
              stock: 0,
              stock_quantity: 0,
              precio_ofima: null,
              iva_ofima: null,
              price: 0,
              tax_rate: 0.19
            }
          } finally {
            newLoadingDetails[item.product_id] = false
            setLoadingDetails(prev => ({ ...prev, [item.product_id]: false }))
          }
        }
      } finally {
        // Marcar todos como no cargando
        uniqueSkus.forEach(sku => {
          newLoadingDetails[sku] = false
          setLoadingDetails(prev => ({ ...prev, [sku]: false }))
        })
      }

      // Calcular dimensiones y pesos usando los datos cargados
      orderDetail.lineItems.forEach(item => {
        const productDetail = newDetails[item.product_id]
        if (productDetail) {
          // Verificar si el producto principal tiene todas las dimensiones
          if (!productDetail.longitud || productDetail.longitud <= 0) {
            todosTienenLargo = false
          } else if (productDetail.longitud > maxLargo) {
            maxLargo = productDetail.longitud
            productoMaxLargo = {
              sku: productDetail.sku,
              nombre: productDetail.name,
              largo: productDetail.longitud
            }
          }

          if (!productDetail.ancho || productDetail.ancho <= 0) {
            todosTienenAncho = false
          } else if (productDetail.ancho > maxAncho) {
            maxAncho = productDetail.ancho
            productoMaxAncho = {
              sku: productDetail.sku,
              nombre: productDetail.name,
              ancho: productDetail.ancho
            }
          }

          if (!productDetail.peso || productDetail.peso <= 0) {
            todosTienenPeso = false
            if (productDetail.peso >= 0 && productDetail.peso <= 8) {
              onTipoServicioChange?.('12')
            }
          } else {
            totalPeso += productDetail.peso * item.quantity
            if (productDetail.peso >= 0 && productDetail.peso <= 8) {
              onTipoServicioChange?.('12')
            }
          }

          if (!productDetail.alto || productDetail.alto <= 0) {
            todosTienenAlto = false
          } else {
            totalAlto += productDetail.alto * item.quantity
          }

          // Si es un combo, procesar componentes
          if (productDetail.is_combo === 1 && productDetail.components) {
            productDetail.components.forEach((component: any) => {
              const componentDetails = newDetails[component.sku]
              if (componentDetails) {
                const cantidadComponente = component.quantity * item.quantity

                if (!componentDetails.longitud || componentDetails.longitud <= 0) {
                  todosTienenLargo = false
                } else if (componentDetails.longitud > maxLargo) {
                  maxLargo = componentDetails.longitud
                  productoMaxLargo = {
                    sku: componentDetails.sku,
                    nombre: componentDetails.name,
                    largo: componentDetails.longitud
                  }
                }

                if (!componentDetails.ancho || componentDetails.ancho <= 0) {
                  todosTienenAncho = false
                } else if (componentDetails.ancho > maxAncho) {
                  maxAncho = componentDetails.ancho
                  productoMaxAncho = {
                    sku: componentDetails.sku,
                    nombre: componentDetails.name,
                    ancho: componentDetails.ancho
                  }
                }

                if (!componentDetails.peso || componentDetails.peso <= 0) {
                  todosTienenPeso = false
                  if (componentDetails.peso >= 0 && componentDetails.peso <= 8) {
                    onTipoServicioChange?.('12')
                  }
                } else {
                  totalPeso += componentDetails.peso * cantidadComponente
                  if (componentDetails.peso >= 0 && componentDetails.peso <= 8) {
                    onTipoServicioChange?.('12')
                  }
                }

                if (!componentDetails.alto || componentDetails.alto <= 0) {
                  todosTienenAlto = false
                } else {
                  totalAlto += componentDetails.alto * cantidadComponente
                }
              }
            })
          }
        }
      })

      // Imprimir resumen de dimensiones
      console.log('📏 ===== RESUMEN DE DIMENSIONES =====')
      if (todosTienenLargo) console.log('📦 Producto con mayor largo:', productoMaxLargo)
      if (todosTienenAncho) console.log('📦 Producto con mayor ancho:', productoMaxAncho)
      if (todosTienenPeso) console.log('⚖️ Peso total de todos los productos:', totalPeso, 'kg')
      if (todosTienenAlto) console.log('📐 Altura total de todos los productos:', totalAlto, 'cm')

      // Actualizar los inputs del formulario
      setFormData(prev => ({
        ...prev,
        largo: todosTienenLargo ? maxLargo : 0,
        ancho: todosTienenAncho ? maxAncho : 0,
        altura: todosTienenAlto ? totalAlto : 0,
        peso: todosTienenPeso ? totalPeso : 0
      }))

      console.log('✅ Todos los detalles de productos cargados:', newDetails)
      setProductDetails(newDetails)
      isLoadingRef.current = false
      onFormReadyChange?.(true)
    } catch (error) {
      console.error('Error general al cargar detalles:', error)
      isLoadingRef.current = false
      onFormReadyChange?.(true) // Marcar como listo incluso si hay error
    }
  }, [orderDetail, setFormData, onTipoServicioChange, onFormReadyChange])

  // Función para calcular totales (alineado con pedidos: IVA 0 si cliente neto)
  const calculateTotals = useCallback(
    (items: any[] = []) => {
      return computeOrderLineTotals(items, productDetails, '0', clienteNeto)
    },
    [productDetails, clienteNeto]
  )

  // Calcular el total que se muestra en la sección de productos (mercancía + envío)
  const getTotalFromProducts = useCallback(() => {
    const lines = orderDetail?.lineItems || []
    if (!lines.length) return 0

    const totals = computeOrderLineTotals(lines, productDetails, '0', clienteNeto)
    return Math.round(parseFloat(totals.total) + parseFloat(orderDetail?.envio || '0'))
  }, [orderDetail, productDetails, clienteNeto])

  // Función para toggle de combos
  const toggleCombo = useCallback(async (productId: string) => {
    setExpandedCombos(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }))

    // Si se está expandiendo el combo, cargar los detalles de los componentes
    if (!expandedCombos[productId]) {
      const productDetail = productDetails[productId]
      if (productDetail?.is_combo === 1 && productDetail?.components) {
        const stocks: number[] = []

        // Cargar detalles de cada componente
        for (const component of productDetail.components) {
          const compSku = component.sku
          if (!productDetails[compSku] && compSku) {
            setLoadingDetails(prev => ({ ...prev, [compSku]: true }))
            try {
              console.log('Cargando detalles del componente por SKU:', compSku)
              const token = localStorage.getItem('accessToken')
              const response = await axiosInstance.get(`/products/${compSku}`, {
                headers: { Authorization: `Bearer ${token}` }
              })
              if (response.data) {
                const mappedComp = mapApiProductToOrderDetails(response.data)
                stocks.push(mappedComp.stock)
                setProductDetails(prev => ({
                  ...prev,
                  [compSku]: mappedComp
                }))
              }
            } catch (error) {
              console.error('Error al cargar el componente:', compSku, error)
              stocks.push(0)
            } finally {
              setLoadingDetails(prev => ({ ...prev, [compSku]: false }))
            }
          } else if (productDetails[compSku]) {
            const stockValue = productDetails[compSku].stock || productDetails[compSku].stock_quantity || 0
            stocks.push(stockValue)
          }
        }

        // Una vez que tenemos todos los stocks, actualizamos el producto principal
        if (stocks.length > 0) {
          const minStock = Math.min(...stocks)
          console.log('Stocks de componentes:', stocks)
          console.log('Stock mínimo para el combo:', minStock)

          setProductDetails(prev => ({
            ...prev,
            [productId]: {
              ...prev[productId],
              stock: minStock,
              stock_quantity: minStock
            }
          }))
        }
      }
    }
  }, [expandedCombos, productDetails])

  return {
    productDetails,
    setProductDetails,
    loadingDetails,
    setLoadingDetails,
    expandedCombos,
    setExpandedCombos,
    loadProductDetails,
    calculateTotals,
    getTotalFromProducts,
    toggleCombo
  }
}

